import { detectTuning, formatCustomTuning, midiToNote, parseCustomTuning, type Tuning } from '../data/tunings.js';

export interface FileTuning {
  /** Catalogue entry, or undefined when the file's tuning is not in the catalogue (stored as `Custom (<notes>)`). */
  tuning?: Tuning;
  /** MIDI numbers, highest string first, of the track the guess is based on. */
  midiHighToLow: number[];
}

const GUITAR_NAME = /guit|gtr|lead|rhythm|solo|clean|dist|gibson|fender/i;
const BASS_NAME = /bass/i;

/**
 * Reads the tuning of the main string track of a Guitar Pro file (FRE-139). Candidates are non-percussion tracks with
 * 6-10 strings (guitars); only when there are none (bass-only file) the 4-8 string tracks are used, preferring names with
 * "bass". Within the candidates guitar-named (or bass-named) tracks form the pool, then the tuning shared by most tracks
 * wins (ties: catalogue tuning, then the first). `forceBass` skips the guitar step (test harness). Null when unreadable.
 */
export async function detectFileTuning(bytes: Uint8Array, opts: { forceBass?: boolean } = {}): Promise<FileTuning | null> {
  try {
    const alphaTab = await import('@coderline/alphatab');
    const score = alphaTab.importer.ScoreLoader.loadScoreFromBytes(bytes, new alphaTab.Settings());
    const withStrings = (min: number, max: number) =>
      score.tracks.filter((t) => {
        const n = t.staves[0]?.tuning?.length ?? 0;
        return !t.staves[0]?.isPercussion && n >= min && n <= max;
      });
    // Guitars (6-10 strings) win whenever the file has any. Only a bass-only file falls back to 4-8 string tracks.
    const guitars = opts.forceBass ? [] : withStrings(6, 10);
    const candidates = guitars.length > 0 ? guitars : withStrings(4, 8);
    const nameRe = guitars.length > 0 ? GUITAR_NAME : BASS_NAME;
    // Named tracks only (when there are any), so default-tuned synths, pianos and vocals cannot outvote them.
    const named = candidates.filter((t) => nameRe.test(t.name));
    const pool = named.length > 0 ? named : candidates;
    // Majority vote on the tuning; ties prefer a catalogue tuning, then the group whose first track comes first.
    const groups = new Map<string, { tracks: typeof pool; known: boolean }>();
    for (const t of pool) {
      const tuning = Array.from(t.staves[0].tuning);
      const key = tuning.join(',');
      const g = groups.get(key) ?? { tracks: [], known: Boolean(detectTuning(tuning)) };
      g.tracks.push(t);
      groups.set(key, g);
    }
    let best: { tracks: typeof pool; known: boolean } | undefined;
    for (const g of groups.values()) {
      if (!best || g.tracks.length > best.tracks.length || (g.tracks.length === best.tracks.length && g.known && !best.known)) best = g;
    }
    const track = best?.tracks[0];
    if (!track) return null;
    const midi = Array.from(track.staves[0].tuning);
    return { tuning: detectTuning(midi), midiHighToLow: midi };
  } catch {
    return null;
  }
}

/** Value to store on a tab for a tuning that is not in the catalogue: `Custom (<notes>)`, or bare "Custom" when unreadable. */
export function customTuningFromMidi(midiHighToLow: number[]): string {
  const notes = [...midiHighToLow].reverse().map((m) => {
    const { note, octave } = midiToNote(m);
    return `${note}${octave}`;
  });
  const stored = formatCustomTuning('Custom', notes);
  return parseCustomTuning(stored) ? stored : 'Custom';
}
